// frontend/src/pages/OvertimeMonitoring/components/OvertimeStatsCards.js
import React from 'react';
import { Users, AlertOctagon, Eye, Clock } from 'lucide-react';

/**
 * Helper function to convert decimal hours to "hr min" format
 * Example: 31.36 → "31h 22min"
 */
const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0h';
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}min`;
};

/**
 * Helper function to format yearMonth from "YYYY-MM" to "MMM YYYY"
 * Example: "2025-12" → "Dec 2025"
 */
const formatYearMonth = (yearMonth) => {
  if (!yearMonth) return 'Current Month';
  
  try {
    const [year, month] = yearMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-SG', { month: 'short', year: 'numeric' });
  } catch (e) {
    return yearMonth;
  }
};

/**
 * OvertimeStatsCards - 4-card summary for 72-hour OT monitoring
 * 
 * Cards:
 * 1. Employees Tracked (blue) - click resets filters
 * 2. Action Required (red) - exceeded + critical with breakdown
 * 3. Watch List (amber) - at_risk + projected_exceed + watch with breakdown
 * 4. OT Summary (purple) - Total OT + Avg OT
 * 
 * Cards 1-3 are clickable and filter the table below.
 */
const OvertimeStatsCards = ({ 
  stats, 
  loading, 
  forecastSummary = null, 
  isCurrentMonth = true,
  onCardClick,
  activeFilter = ''
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
              <div className="ml-2 sm:ml-4 flex-1 min-w-0">
                <div className="h-7 bg-gray-200 rounded w-16 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-24" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // Compute grouped counts
  const exceededCount = stats.exceededCount || 0;
  const criticalCount = stats.criticalCount || 0;
  const atRiskCount = stats.atRiskCount || 0;
  const projectedToExceed = (isCurrentMonth && forecastSummary?.projectedToExceed) || 0;
  const watchCount = (isCurrentMonth && forecastSummary?.watchCount) || 0;

  const actionRequiredCount = exceededCount + criticalCount;
  const watchListCount = atRiskCount + projectedToExceed + watchCount;

  const totalOtHours = stats.currentMonth?.totalOvertimeHours || 0;
  const avgOtHours = stats.currentMonth?.averageOvertimeHours || 0;

  // Build breakdown strings
  const actionBreakdown = [
    exceededCount > 0 && `${exceededCount} exceeded`,
    criticalCount > 0 && `${criticalCount} critical`
  ].filter(Boolean).join(' · ') || 'All clear';

  const watchBreakdownParts = [
    atRiskCount > 0 && `${atRiskCount} at risk`,
    projectedToExceed > 0 && `${projectedToExceed} projected`,
    watchCount > 0 && `${watchCount} watch`
  ].filter(Boolean);
  const watchBreakdown = watchBreakdownParts.length > 0
    ? watchBreakdownParts.join(' · ')
    : 'All clear';

  const handleClick = (filterValue) => {
    if (!onCardClick) return;
    // Toggle off if already active
    if (activeFilter === filterValue) {
      onCardClick('');
    } else {
      onCardClick(filterValue);
    }
  };

  // Card definitions
  const cards = [
    {
      id: 'total',
      filterValue: '',
      label: 'Employees Tracked',
      value: stats.currentMonth?.totalEmployees || 0,
      icon: Users,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      ringColor: 'ring-blue-500',
      subtitle: formatYearMonth(stats.currentMonth?.yearMonth),
      isActive: activeFilter === ''
    },
    {
      id: 'action_required',
      filterValue: 'action_required',
      label: 'Action Required',
      value: actionRequiredCount,
      icon: AlertOctagon,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      ringColor: 'ring-red-500',
      subtitle: actionBreakdown,
      isActive: activeFilter === 'action_required',
      urgent: actionRequiredCount > 0
    },
    {
      id: 'watch_list',
      filterValue: 'watch_list',
      label: 'Watch List',
      value: watchListCount,
      icon: Eye,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      ringColor: 'ring-amber-500',
      subtitle: watchBreakdown,
      isActive: activeFilter === 'watch_list'
    }
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
      {/* Clickable metric cards */}
      {cards.map(card => {
        const Icon = card.icon;
        return (
          <button
            key={card.id}
            onClick={() => handleClick(card.filterValue)}
            className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 text-left transition-all duration-200 hover:shadow-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${card.ringColor} ${
              card.isActive && card.filterValue !== '' ? `ring-2 ${card.ringColor}` : ''
            }`}
          >
            <div className="flex items-center">
              <div className={`w-9 h-9 sm:w-12 sm:h-12 ${card.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${card.iconColor}`} />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className={`text-lg sm:text-2xl font-bold ${card.urgent ? 'text-red-600' : 'text-gray-900'}`}>
                  {card.value}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{card.label}</p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 truncate">{card.subtitle}</p>
            </div>
          </button>
        );
      })}

      {/* OT Summary card (not clickable) */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-center">
          <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
          </div>
          <div className="ml-2 sm:ml-4 flex-1 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 pr-1 sm:pr-3">
              <div className="flex-shrink-0 min-w-0">
                <p className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                  {formatHoursMinutes(totalOtHours)}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 whitespace-nowrap">Total OT</p>
              </div>
              <div className="w-px h-8 sm:h-10 bg-gray-200 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                  {formatHoursMinutes(avgOtHours)}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Avg/Worker</p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">OT Summary</p>
        </div>
      </div>
    </div>
  );
};

export default OvertimeStatsCards;
